import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { 
  Container, 
  CircularProgress, 
  Box, 
  Grid, 
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import { 
  EmailOutlined, 
  EventAvailable, 
  PhoneEnabled, 
  LinkedIn as LinkedInIcon,
  TrendingUp 
} from '@mui/icons-material';
import { useAuth } from '../lib/supabase/AuthContext';
import { getUserRole } from '../lib/supabase/userService';
import { UserRole } from '../types/admin';
import { KPICard } from '../components/KPICard';
import PeriodFilter from '../components/PeriodFilter';
import { PeriodFilter as PeriodFilterType } from '../types/rdv';
import { calculateFilteredKPI } from '../data/dashboardData';
import { getCampaigns } from '../lib/supabase/campaignsService';
import { getLeads } from '../lib/supabase/leadsService';
import { Database } from '../types/supabase';

type Campaign = Database['public']['Tables']['campaigns']['Row'];

interface CampaignStats {
  id: string;
  name: string;
  status: string;
  rdvPris: number;
  mailsEnvoyes: number;
  messagesLinkedIn: number;
  appelsConnectes: number;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<PeriodFilterType>('tous');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [kpiData, setKpiData] = useState({
    rdvPris: 0,
    mer: 0,
    mailsEnvoyes: 0,
    messagesLinkedIn: 0,
    appelsConnectes: 0
  });
  const [campaignStats, setCampaignStats] = useState<CampaignStats[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data } = await getUserRole(user.id);
        setUserRole(data);
        
        // Si c'est un client, charger les statistiques
        if (data === 'client') {
          await loadKPIData();
          await loadCampaignStats();
        }
      } catch (error) {
        console.error('Erreur lors du chargement du rôle:', error);
        setUserRole('client'); // Par défaut client en cas d'erreur
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [user]);

  // Recharger les KPI quand la période change
  useEffect(() => {
    if (userRole === 'client') {
      loadKPIData();
      loadCampaignStats();
    }
  }, [period, startDate, endDate, userRole]);

  const loadKPIData = async () => {
    try {
      const calculatedKPI = await calculateFilteredKPI(period, startDate, endDate);
      setKpiData(calculatedKPI);
    } catch (error) {
      console.error('Erreur lors du chargement des KPI:', error);
    }
  };

  const loadCampaignStats = async () => {
    if (!user) return;
    
    setLoadingCampaigns(true);
    try {
      const { data: campaigns } = await getCampaigns(user.id);
      
      if (campaigns) {
        const statsPromises = campaigns.map(async (campaign: Campaign) => {
          const { data: leads } = await getLeads(campaign.id, user.id);
          
          if (leads) {
            // Filtrer les leads par période si nécessaire
            let filteredLeads = leads;
            
            if (period !== 'tous') {
              const now = new Date();
              let dateRange: { start: Date; end: Date };
              
              switch (period) {
                case 'jour':
                  dateRange = {
                    start: new Date(now.setHours(0, 0, 0, 0)),
                    end: new Date(now.setHours(23, 59, 59, 999)),
                  };
                  break;
                case 'semaine':
                  const day = now.getDay();
                  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
                  dateRange = {
                    start: new Date(new Date(now.setDate(diff)).setHours(0, 0, 0, 0)),
                    end: new Date(new Date(now.setDate(diff + 6)).setHours(23, 59, 59, 999)),
                  };
                  break;
                case 'mois':
                  dateRange = {
                    start: new Date(now.getFullYear(), now.getMonth(), 1),
                    end: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999),
                  };
                  break;
                case 'personnalise':
                  if (startDate && endDate) {
                    dateRange = { start: startDate, end: endDate };
                    break;
                  }
                  filteredLeads = leads;
                  break;
                default:
                  filteredLeads = leads;
              }
              
              if (period !== 'personnalise' || (startDate && endDate)) {
                filteredLeads = leads.filter(lead => {
                  if (!lead.created_at) return false;
                  const leadDate = new Date(lead.created_at);
                  return leadDate >= dateRange!.start && leadDate <= dateRange!.end;
                });
              }
            }
            
            const rdvLeads = filteredLeads.filter(lead => lead.rdv === true);
            const rdvPris = rdvLeads.length;
            
            // Utiliser les statistiques de la campagne ou calculer depuis les leads
            const mailsEnvoyes = campaign.nb_mails_envoyes || 0;
            const messagesLinkedIn = campaign.nb_messages_linkedin || 0;
            const appelsConnectes = campaign.nb_appels || 0;
            
            return {
              id: campaign.id,
              name: campaign.nom,
              status: campaign.statut || 'preparation',
              rdvPris,
              mailsEnvoyes,
              messagesLinkedIn,
              appelsConnectes
            };
          }
          
          return {
            id: campaign.id,
            name: campaign.nom,
            status: campaign.statut || 'preparation',
            rdvPris: 0,
            mailsEnvoyes: campaign.nb_mails_envoyes || 0,
            messagesLinkedIn: campaign.nb_messages_linkedin || 0,
            appelsConnectes: campaign.nb_appels || 0
          };
        });
        
        const stats = await Promise.all(statsPromises);
        setCampaignStats(stats);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des stats des campagnes:', error);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const handleDateChange = (start: Date | null, end: Date | null) => {
    setStartDate(start);
    setEndDate(end);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'en cours':
      case 'actif':
      case 'active':
      case 'lance':
      case 'lancé':
        return 'success'; // Vert
      case 'preparation':
      case 'préparation':
      case 'en preparation':
      case 'en préparation':
      case 'draft':
      case 'brouillon':
      case 'en attente':
        return 'warning'; // Orange
      case 'stop':
      case 'stopped':
      case 'arrêté':
      case 'arrete':
      case 'pause':
      case 'paused':
      case 'terminé':
      case 'termine':
      case 'completed':
        return 'error'; // Rouge
      default:
        return 'warning'; // Orange par défaut
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status.toLowerCase()) {
      case 'en cours':
      case 'actif':
      case 'active':
      case 'lance':
      case 'lancé':
        return 'En cours';
      case 'preparation':
      case 'préparation':
      case 'en preparation':
      case 'en préparation':
      case 'draft':
      case 'brouillon':
      case 'en attente':
        return 'En préparation';
      case 'stop':
      case 'stopped':
      case 'arrêté':
      case 'arrete':
      case 'pause':
      case 'paused':
      case 'terminé':
      case 'termine':
      case 'completed':
        return 'Stop';
      default:
        return 'En préparation';
    }
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirection selon le rôle pour admin et caller
  if (userRole === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  
  if (userRole === 'caller') {
    return <Navigate to="/caller" replace />;
  }

  // Dashboard pour les clients
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      
      {/* Filtre de période */}
      <Box sx={{ mb: 4 }}>
        <PeriodFilter
          period={period}
          onPeriodChange={setPeriod}
          startDate={startDate}
          endDate={endDate}
          onDateChange={handleDateChange}
        />
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="RDV Pris"
            value={kpiData.rdvPris}
            icon={<EventAvailable />}
            color="primary"
            delay={0}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Mails Envoyés"
            value={kpiData.mailsEnvoyes}
            icon={<EmailOutlined />}
            color="info"
            delay={100}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Messages LinkedIn"
            value={kpiData.messagesLinkedIn}
            icon={<LinkedInIcon />}
            color="primary"
            delay={200}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard
            title="Appels Connectés"
            value={kpiData.appelsConnectes}
            icon={<PhoneEnabled />}
            color="warning"
            delay={300}
          />
        </Grid>
      </Grid>

      {/* Tableau des statistiques par campagne */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Statistiques par Campagne
        </Typography>
        
        {loadingCampaigns ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Campagne</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">RDV Pris</TableCell>
                  <TableCell align="right">Mails Envoyés</TableCell>
                  <TableCell align="right">Messages LinkedIn</TableCell>
                  <TableCell align="right">Appels Connectés</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaignStats.map((campaign) => (
                  <TableRow key={campaign.id}>
                    <TableCell component="th" scope="row">
                      <Typography variant="body2" fontWeight="bold">
                        {campaign.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(campaign.status)}
                        color={getStatusColor(campaign.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      {campaign.rdvPris}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'info.main' }}>
                      {campaign.mailsEnvoyes}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'primary.main' }}>
                      {campaign.messagesLinkedIn}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'warning.main' }}>
                      {campaign.appelsConnectes}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        
        {campaignStats.length === 0 && !loadingCampaigns && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="textSecondary">
              Aucune campagne trouvée pour la période sélectionnée
            </Typography>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default Dashboard; 